import React, { useState, useEffect, useCallback, useRef } from 'react';

/**
 * BreakModal Component
 * 
 * Allows drivers to take breaks before the scheduled break time.
 * Handles break eligibility checking and split shift requests.
 * 
 * Break Eligibility Rules:
 * - Cannot take break if currentWaypoint is a dropoff (passenger loaded)
 * - Must have enough time before next pickup (breakBufferMins)
 * 
 * Split Shift Eligibility:
 * - At scheduled break time: drive to route start + 2 hours + drive to next pickup
 *   must be <= next pickup scheduled time
 * 
 * Uses Google Maps Distance Matrix API for drive time calculations with traffic.
 */

const BreakModal = ({
  isOpen,
  onClose,
  breakStep,
  itinerary,
  currentWaypoint,
  vehicleLocation,
  fleetSettings,
  googleMapsLoaded,
  routeDetails,
  onStartBreak,
  onRequestSplitShift
}) => {
  const [calculating, setCalculating] = useState(true);
  const [eligibility, setEligibility] = useState({
    canTakeBreak: false,
    reason: '',
    splitShiftEligible: false,
    splitShiftReason: ''
  });
  const calculatedRef = useRef(false);

  // Get fleet settings with defaults
  const breakBufferMins = fleetSettings?.breakBufferMins ?? 5;
  const breakDuration = breakStep?.duration || breakStep?.Duration || breakStep?.Service || 1800; // seconds

  /**
   * Calculate drive time using Google Maps Distance Matrix API
   * @param {Object} origin - { lat, lng }
   * @param {Object} destination - { lat, lng }
   * @param {Date} departureTime - When to depart (for traffic calculation)
   * @returns {Promise<number>} - Drive time in seconds
   */
  const calculateDriveTime = useCallback(async (origin, destination, departureTime) => {
    if (!googleMapsLoaded || !window.google?.maps?.DistanceMatrixService) {
      console.warn('Google Maps not loaded, estimating drive time');
      // Fallback: estimate 30 mph average = 0.5 miles per minute
      const R = 3959; // Earth radius in miles
      const dLat = (destination.lat - origin.lat) * Math.PI / 180;
      const dLon = (destination.lng - origin.lng) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(origin.lat * Math.PI / 180) * Math.cos(destination.lat * Math.PI / 180) *
                Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const distance = R * c; // miles
      return (distance / 30) * 60 * 60; // seconds at 30 mph
    }

    return new Promise((resolve, reject) => {
      const service = new window.google.maps.DistanceMatrixService();
      
      service.getDistanceMatrix({
        origins: [new window.google.maps.LatLng(origin.lat, origin.lng)],
        destinations: [new window.google.maps.LatLng(destination.lat, destination.lng)],
        travelMode: window.google.maps.TravelMode.DRIVING,
        drivingOptions: {
          departureTime: departureTime,
          trafficModel: window.google.maps.TrafficModel.BEST_GUESS
        }
      }, (response, status) => {
        if (status === 'OK' && response?.rows?.[0]?.elements?.[0]?.status === 'OK') {
          const durationInTraffic = response.rows[0].elements[0].duration_in_traffic?.value;
          const duration = response.rows[0].elements[0].duration?.value;
          resolve(durationInTraffic || duration || 0);
        } else {
          console.warn('Distance Matrix API error:', status, response);
          reject(new Error('Failed to calculate drive time'));
        }
      });
    });
  }, [googleMapsLoaded]);

  /**
   * Check break eligibility and split shift eligibility
   */
  const checkEligibility = useCallback(async () => {
    if (!breakStep || !itinerary || !currentWaypoint || !vehicleLocation) {
      setEligibility({
        canTakeBreak: false,
        reason: 'Missing required information',
        splitShiftEligible: false,
        splitShiftReason: 'Missing required information'
      });
      setCalculating(false);
      return;
    }

    const now = new Date();
    const breakScheduledTime = new Date(breakStep.eta || breakStep.ArrivalDateTime);

    // JUNE 2026: Removed the "break time has already passed" rule. With itinerary steps
    // ordered by Sequence, a running-late route legitimately reaches its break after the
    // scheduled window; blocking here made the break vanish with no options. Eligibility is
    // now governed only by passenger-in-vehicle and next-pickup spacing.

    // RULE 2: Cannot take break if currentWaypoint is a dropoff (passenger in vehicle)
    const currentType = (currentWaypoint.type || '').toLowerCase();
    if (currentType === 'delivery' || currentType === 'dropoff') {
      setEligibility({
        canTakeBreak: false,
        reason: 'Cannot take break with passenger in vehicle. Complete current dropoff first.',
        splitShiftEligible: false,
        splitShiftReason: 'Cannot request split shift with passenger in vehicle'
      });
      setCalculating(false);
      return;
    }

    // Find next pickup step after break
    const breakIndex = itinerary.findIndex(step => 
      step.StepId === breakStep.StepId || step.stepId === breakStep.stepId
    );
    
    const nextPickupAfterBreak = itinerary.slice(breakIndex + 1).find(step => {
      const stepType = (step.type || '').toLowerCase();
      return stepType === 'pickup' && !step.isCompleted;
    });

    if (!nextPickupAfterBreak) {
      // No pickups after break - break is allowed
      setEligibility({
        canTakeBreak: true,
        reason: '',
        splitShiftEligible: false,
        splitShiftReason: 'No trips scheduled after break'
      });
      setCalculating(false);
      return;
    }

    const nextPickupTime = new Date(nextPickupAfterBreak.trip?.pickup?.date || nextPickupAfterBreak.eta);
    
    try {
      // Calculate time needed for break (current time + break duration + buffer)
      const breakRequiredEndTime = new Date(now.getTime() + (breakDuration * 1000) + (breakBufferMins * 60 * 1000));

      let canTakeBreak = true;
      let breakReason = '';

      // Check if there's enough time before next pickup
      if (breakRequiredEndTime > nextPickupTime) {
        canTakeBreak = false;
        const shortfall = Math.ceil((breakRequiredEndTime - nextPickupTime) / 60000);
        breakReason = `Not enough time before next pickup. Need ${shortfall} more minutes.`;
      }

      // Check split shift eligibility
      // At scheduled break time: drive to start + 2 hours + drive to next pickup <= next pickup time
      let splitShiftEligible = false;
      let splitShiftReason = '';

      const routeStartLocation = {
        lat: routeDetails?.startLat || routeDetails?.StartLatitude,
        lng: routeDetails?.startLon || routeDetails?.StartLongitude
      };

      const nextPickupLocation = {
        lat: nextPickupAfterBreak.Latitude || nextPickupAfterBreak.latitude,
        lng: nextPickupAfterBreak.Longitude || nextPickupAfterBreak.longitude
      };

      if (!routeStartLocation.lat || !routeStartLocation.lng) {
        splitShiftReason = 'Route start location not available';
      } else if (!nextPickupLocation.lat || !nextPickupLocation.lng) {
        splitShiftReason = 'Next pickup location not available';
      } else {
        try {
          // Drive time from current location to route start (at break scheduled time)
          const driveToStart = await calculateDriveTime(
            vehicleLocation,
            routeStartLocation,
            breakScheduledTime
          );

          // Drive time from route start to next pickup (at break scheduled time + 2 hours)
          const twoHoursLater = new Date(breakScheduledTime.getTime() + (2 * 60 * 60 * 1000));
          const driveToPickup = await calculateDriveTime(
            routeStartLocation,
            nextPickupLocation,
            twoHoursLater
          );

          // Total time: drive to start + 2 hours + drive to pickup + buffer
          const totalSplitShiftTime = driveToStart + (2 * 60 * 60) + driveToPickup + (breakBufferMins * 60);
          const splitShiftEndTime = new Date(breakScheduledTime.getTime() + (totalSplitShiftTime * 1000));

          if (splitShiftEndTime <= nextPickupTime) {
            splitShiftEligible = true;
            splitShiftReason = `2-hour break with travel: ${Math.ceil(driveToStart/60)} min to depot, 2 hrs off, ${Math.ceil(driveToPickup/60)} min to pickup`;
          } else {
            const shortfall = Math.ceil((splitShiftEndTime - nextPickupTime) / 60000);
            splitShiftReason = `Split shift would end ${shortfall} minutes after next pickup time`;
          }
        } catch (err) {
          console.error('Error calculating split shift times:', err);
          splitShiftReason = 'Unable to calculate drive times';
        }
      }

      setEligibility({
        canTakeBreak,
        reason: breakReason,
        splitShiftEligible,
        splitShiftReason
      });
    } catch (err) {
      console.error('Error checking eligibility:', err);
      setEligibility({
        canTakeBreak: false,
        reason: 'Error calculating break eligibility',
        splitShiftEligible: false,
        splitShiftReason: 'Error calculating split shift eligibility'
      });
    }

    setCalculating(false);
  }, [breakStep, itinerary, currentWaypoint, vehicleLocation, breakDuration, breakBufferMins, routeDetails, calculateDriveTime]);

  // Run eligibility check once when modal opens
  useEffect(() => {
    if (isOpen && !calculatedRef.current) {
      calculatedRef.current = true;
      setCalculating(true);
      checkEligibility();
    }
    
    // Reset when modal closes
    if (!isOpen) {
      calculatedRef.current = false;
    }
  }, [isOpen, checkEligibility]);

  const handleStartBreak = () => {
    if (onStartBreak && eligibility.canTakeBreak) {
      onStartBreak(breakStep);
      onClose();
    }
  };

  const handleRequestSplitShift = () => {
    if (onRequestSplitShift && eligibility.splitShiftEligible) {
      onRequestSplitShift(breakStep);
      onClose();
    }
  };

  if (!isOpen) return null;

  const breakDurationMins = Math.floor(breakDuration / 60);
  const scheduledTime = breakStep?.eta || breakStep?.ArrivalDateTime;
  const formattedTime = scheduledTime 
    ? new Date(scheduledTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : 'N/A';

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2200]"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Take a Break</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>

        {/* Break Info */}
        <div className="bg-blue-50 rounded-lg p-4 mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-600">Scheduled Time:</span>
            <span className="font-semibold">{formattedTime}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Duration:</span>
            <span className="font-semibold">{breakDurationMins} minutes</span>
          </div>
        </div>

        {calculating ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Checking break eligibility...</p>
          </div>
        ) : (
          <>
            {/* Regular Break Section */}
            <div className="mb-4">
              <h3 className="font-semibold text-gray-700 mb-2">Regular Break</h3>
              {eligibility.canTakeBreak ? (
                <button
                  onClick={handleStartBreak}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition-colors"
                >
                  Start Break Now
                </button>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-700 text-sm">{eligibility.reason}</p>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="flex items-center my-4">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="px-3 text-gray-500 text-sm">OR</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>

            {/* Split Shift Section */}
            <div className="mb-4">
              <h3 className="font-semibold text-gray-700 mb-2">Split Shift (2+ Hours)</h3>
              <p className="text-sm text-gray-500 mb-2">
                Request to clock out and return later to complete your route.
              </p>
              {eligibility.splitShiftEligible ? (
                <>
                  <p className="text-sm text-green-600 mb-2">{eligibility.splitShiftReason}</p>
                  <button
                    onClick={handleRequestSplitShift}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-4 rounded-lg transition-colors"
                  >
                    Request Split Shift
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Requires dispatcher approval
                  </p>
                </>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-700 text-sm">{eligibility.splitShiftReason}</p>
                </div>
              )}
            </div>

            {/* Cancel Button */}
            <button
              onClick={onClose}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BreakModal;
